import { useQuery, NetworkStatus } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import { useFragment as getFragmentData } from '@/graphql';
import type { ContactsQuery, ContactsQueryVariables } from '@/graphql/types';
import { ContactsDocument, ContactFieldsFragmentDoc } from '@/graphql/types';
import type { UseOrderAndPaginationControlReturn } from '../../../table/hooks';
import { createAfterCursor, extractNodes } from '../../../../utils/relay';

export function useContactQueryGenerator(
    orderAndPaginationControl: UseOrderAndPaginationControlReturn,
    search: string
) {
    const { pageSize, currentPage, orderBy, setTotalCount } = orderAndPaginationControl;

    const variables = useMemo(
        () => ({
            orderBy: orderBy || undefined,
            after: createAfterCursor(currentPage, pageSize),
            first: pageSize,
            search: search || undefined,
        }),
        [orderBy, currentPage, pageSize, search]
    );

    const { data, loading, refetch, networkStatus, error } = useQuery<
        ContactsQuery,
        ContactsQueryVariables
    >(ContactsDocument, {
        variables,
        // Prioritize cache for instant rendering and reduced bandwidth.
        // Safe because contacts are private to the user and unlikely to change externally.
        // Freshness is maintained by explicit mutation re-fetches rather than background polling.
        fetchPolicy: 'cache-first',
    });

    useEffect(() => {
        setTotalCount(data?.contacts?.totalCount ?? 0);
    }, [data?.contacts?.totalCount, setTotalCount]);

    const contacts = useMemo(() => {
        // Flatten Relay edges into a flat array of masked nodes.
        const maskedNodes = extractNodes(data?.contacts?.edges);

        // Unmask fragment data to resolve masked nodes into ContactFieldsFragment objects.
        return getFragmentData(ContactFieldsFragmentDoc, maskedNodes);
    }, [data?.contacts?.edges]);

    return {
        contacts,
        loading: loading && networkStatus === NetworkStatus.loading,
        updating: loading && networkStatus === NetworkStatus.setVariables,
        refetch,
        error,
    };
}
